import type { Control, FieldErrors } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FieldRequiredIndicator,
  FieldRoot,
  Flex,
  Input,
  Stack,
  Textarea,
} from '@enechain/ecloud-designsystem'

import {
  FieldErrorText,
  FieldHelperText,
  FieldLabel,
} from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { CharCount } from '~/pages/AdminPlantUnitMaster/components/CharCount'
import {
  GENERATION_SITE_NAME_ABBR_MAX_LENGTH,
  GENERATION_SITE_NAME_MAX_LENGTH,
  GENERATION_SITE_NOTE_MAX_LENGTH,
  type GenerationSiteFormInput,
} from '~/pages/AdminPlantUnitMaster/models/siteForm'

type Props = {
  control: Control<GenerationSiteFormInput>
  disabled?: boolean
  errors: FieldErrors<GenerationSiteFormInput>
}

/** 発電所 (サイト) の登録・編集ダイアログで共通の入力欄。 */
export const GenerationSiteFormFields = ({
  control,
  errors,
  disabled = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const areaCodeOptions = AREA_CODE_OPTIONS.map((code) => ({
    value: String(code),
    label: convertAreaLabel(code),
  }))

  return (
    <Stack gap={4}>
      <Controller
        control={control}
        name="siteName"
        render={({ field }) => (
          <FieldRoot
            alignItems="stretch"
            invalid={errors.siteName !== undefined}
            required
          >
            <Flex alignItems="center" justifyContent="space-between">
              <FieldLabel htmlFor="generation-site-name">
                {t('page:adminPlantUnitMaster.site.field.siteName')}
                <FieldRequiredIndicator />
              </FieldLabel>
              <CharCount
                max={GENERATION_SITE_NAME_MAX_LENGTH}
                value={field.value}
              />
            </Flex>
            <Input {...field} disabled={disabled} id="generation-site-name" />
            {errors.siteName !== undefined && (
              <FieldErrorText>{errors.siteName.message}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />

      <Controller
        control={control}
        name="siteNameAbbr"
        render={({ field }) => (
          <FieldRoot
            alignItems="stretch"
            invalid={errors.siteNameAbbr !== undefined}
          >
            <Flex alignItems="center" justifyContent="space-between">
              <FieldLabel htmlFor="generation-site-name-abbr">
                {t('page:adminPlantUnitMaster.site.field.siteNameAbbr')}
              </FieldLabel>
              <CharCount
                max={GENERATION_SITE_NAME_ABBR_MAX_LENGTH}
                value={field.value}
              />
            </Flex>
            <Input
              {...field}
              disabled={disabled}
              id="generation-site-name-abbr"
            />
            {errors.siteNameAbbr !== undefined && (
              <FieldErrorText>{errors.siteNameAbbr.message}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />

      <FieldRoot
        alignItems="stretch"
        invalid={errors.areaCode !== undefined}
        required
      >
        <FieldLabel>
          {t('page:adminPlantUnitMaster.site.field.areaCode')}
          <FieldRequiredIndicator />
        </FieldLabel>
        <FormSelect
          aria-label={t('page:adminPlantUnitMaster.site.field.areaCode')}
          control={control}
          isDisabled={disabled}
          name="areaCode"
          numeric
          options={areaCodeOptions}
        />
        <FieldHelperText>
          {t('page:adminPlantUnitMaster.site.field.areaHint')}
        </FieldHelperText>
        {errors.areaCode !== undefined && (
          <FieldErrorText>{errors.areaCode.message}</FieldErrorText>
        )}
      </FieldRoot>

      <Controller
        control={control}
        name="note"
        render={({ field }) => (
          <FieldRoot alignItems="stretch" invalid={errors.note !== undefined}>
            <Flex alignItems="center" justifyContent="space-between">
              <FieldLabel htmlFor="generation-site-note">
                {t('page:adminPlantUnitMaster.site.field.note')}
              </FieldLabel>
              <CharCount
                max={GENERATION_SITE_NOTE_MAX_LENGTH}
                value={field.value}
              />
            </Flex>
            <Textarea
              {...field}
              disabled={disabled}
              id="generation-site-note"
            />
            {errors.note !== undefined && (
              <FieldErrorText>{errors.note.message}</FieldErrorText>
            )}
          </FieldRoot>
        )}
      />
    </Stack>
  )
}
